import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { Matter, Contact, ContactInfo, MatterContactLink } from '@/lib/conflix';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from './ui/card';
import { Button } from './ui/button';
import { LoadingSpinner } from './ui/loading-spinner';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "./ui/table";
import { Input } from './ui/input';
import { 
  Dialog, 
  DialogContent, 
  DialogHeader, 
  DialogTitle, 
  DialogFooter,
  DialogClose
} from './ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "./ui/select";

const apiUrl = process.env.REACT_APP_API_URL;

function MatterDetail({ idToken}: { idToken: string; }) {
  const [matter, setMatter] = useState<Matter | null>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string>('');
  const [linkedContacts, setLinkedContacts] = useState<MatterContactLink[]>([]);
  const [loadingContacts, setLoadingContacts] = useState<boolean>(false);
  const [editDialogOpen, setEditDialogOpen] = useState<boolean>(false);
  const [editedMatter, setEditedMatter] = useState<Matter | null>(null);
  const [updateLoading, setUpdateLoading] = useState<boolean>(false);

  useEffect(() => {
    const fetchMatter = async () => {
      try {
        // Get matter ID from URL
        const url = new URL(window.location.href);
        const pathParts = url.pathname.split('/');
        const matterId = pathParts[pathParts.length - 1];

        if (!matterId) {
          setError('Matter ID not found in URL');
          setLoading(false);
          return;
        }

        // Fetch matter details
        const response = await axios.get(
          `${apiUrl}matters/${matterId}`,
          {
            headers: {
              "Authorization": idToken
            }
          }
        );

        setMatter(response.data);
        setLoading(false);

        // After getting matter, fetch linked contacts
        await fetchLinkedContacts(matterId);
      } catch (error) {
        console.error('Error fetching matter:', error);
        setError('Failed to load matter details');
        setLoading(false);
      }
    };

    const fetchLinkedContacts = async (matterId: string) => {
      try {
        setLoadingContacts(true);
        const response = await axios.get(
          `${apiUrl}matters/${matterId}/contacts`,
          {
            headers: {
              "Authorization": idToken
            }
          }
        );
        
        // Special handling if it's a single object and not an array
        if (response.data && typeof response.data === 'object' && !Array.isArray(response.data)) {
            const dataAsArray = [response.data];
            setLinkedContacts(dataAsArray);
            setLoadingContacts(false);
            return;
        }
        
        // Validate and sanitize the response data
        if (Array.isArray(response.data)) {
          // Just filter out null/undefined values
          const filteredContacts = response.data.filter(item => item != null && typeof item === 'object');
          setLinkedContacts(filteredContacts);
        } else {
          console.warn('Expected array of contacts, but received:', response.data);
          setLinkedContacts([]);
        }
        
        setLoadingContacts(false);
      } catch (error) {
        console.error('Error fetching linked contacts:', error);
        setLinkedContacts([]);
        setLoadingContacts(false);
      }
    };

    fetchMatter();
  }, [idToken]);

  const handleBack = () => {
    window.history.pushState({}, '', '/');
    // Trigger a popstate event to update the view in App.tsx
    window.dispatchEvent(new Event('popstate'));
  };
  
  const handleOpenEditDialog = () => {
    if (matter) {
      setEditedMatter({...matter});
      setEditDialogOpen(true);
    }
  };
  
  const handleInputChange = (field: keyof Matter, value: string) => {
    if (editedMatter) {
      setEditedMatter({
        ...editedMatter,
        [field]: value
      });
    }
  };

  const handleUpdateMatter = async () => {
    if (!editedMatter || !matter) return;
    
    try {
      setUpdateLoading(true);
      const response = await axios.put(
        `${apiUrl}matters/${matter.matterId}`,
        {
          name: editedMatter.name,
          referenceNumber: editedMatter.referenceNumber,
          status: editedMatter.status
        },
        {
          headers: {
            "Content-Type": "application/json",
            "Authorization": idToken
          }
        }
      );
      
      // Update the local state with the updated matter
      setMatter(response.data);
      setEditDialogOpen(false);
    } catch (error) {
      console.error('Error updating matter:', error);
      setError('Failed to update matter details');
    } finally {
      setUpdateLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="container mx-auto grid justify-items-center">
        <LoadingSpinner size={150} />
        <div>Loading matter details...</div>
      </div>
    );
  }

  if (error) {
    return (
      <Card className="max-w-2xl mx-auto my-8">
        <CardHeader>
          <CardTitle>Error</CardTitle>
          <CardDescription className="text-red-600">{error}</CardDescription>
        </CardHeader>
        <CardContent>
          <Button onClick={handleBack}>Back to Home</Button>
        </CardContent>
      </Card>
    );
  }

  if (!matter) {
    return (
      <Card className="max-w-2xl mx-auto my-8">
        <CardHeader>
          <CardTitle>Matter Not Found</CardTitle>
          <CardDescription>The requested matter could not be found.</CardDescription>
        </CardHeader>
        <CardContent>
          <Button onClick={handleBack}>Back to Home</Button>
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="max-w-4xl mx-auto my-8 space-y-6">
      {/* Matter Details Card */}
      <Card>
        <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
          <div>
            <CardTitle>{matter.name}</CardTitle>
            <CardDescription>Reference Number: {matter.referenceNumber}</CardDescription>
          </div>
          <Button 
            variant="outline" 
            size="sm" 
            onClick={handleOpenEditDialog}
          >
            Edit
          </Button>
        </CardHeader>
        <CardContent>
          <div className="grid gap-4">
            <div className="grid grid-cols-2 gap-2">
              <div className="font-semibold">Matter ID:</div>
              <div>{matter.matterId}</div>
            </div>
            <div className="grid grid-cols-2 gap-2">
              <div className="font-semibold">Created At:</div>
              <div>{new Date(matter.createdAt).toLocaleDateString()}</div>
            </div>
            <div className="grid grid-cols-2 gap-2">
              <div className="font-semibold">Status:</div>
              <div>{matter.status}</div>
            </div>
            {/* Add more fields as needed based on your Matter type */}
          </div>
        </CardContent>
      </Card>
      
      {/* Edit Matter Dialog */}
      <Dialog open={editDialogOpen} onOpenChange={setEditDialogOpen}>
        <DialogContent className="sm:max-w-[425px]">
          <DialogHeader>
            <DialogTitle>Edit Matter Details</DialogTitle>
          </DialogHeader>
          <div className="grid gap-4 py-4">
            <div className="grid grid-cols-4 items-center gap-4">
              <label htmlFor="name" className="text-right font-medium">
                Name
              </label>
              <Input
                id="name"
                value={editedMatter?.name || ''}
                onChange={(e) => handleInputChange('name', e.target.value)}
                className="col-span-3"
              />
            </div>
            <div className="grid grid-cols-4 items-center gap-4">
              <label htmlFor="referenceNumber" className="text-right font-medium">
                Reference Number
              </label>
              <Input
                id="referenceNumber"
                value={editedMatter?.referenceNumber || ''}
                onChange={(e) => handleInputChange('referenceNumber', e.target.value)}
                className="col-span-3"
              />
            </div>
            <div className="grid grid-cols-4 items-center gap-4">
              <label htmlFor="status" className="text-right font-medium">
                Status
              </label>
              <Select
                    value={editedMatter?.status || ''}
                    onValueChange={(value) => handleInputChange('status', value)}
                >
                  <SelectTrigger className="w-full">
                      <SelectValue placeholder="Select Status" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="Open">Open</SelectItem>
                    <SelectItem value="Closed">Closed</SelectItem>
                  </SelectContent>
              </Select>
            </div>
          </div>
          <DialogFooter>
            <Button
              type="submit"
              onClick={handleUpdateMatter}
              disabled={updateLoading}
            >
              {updateLoading ? 'Saving...' : 'Save changes'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Linked Contacts Section */}
      <Card>
        <CardHeader>
          <CardTitle>Linked Contacts</CardTitle>
          <CardDescription>People and organizations associated with this matter</CardDescription>
        </CardHeader>
        <CardContent>
          {loadingContacts ? (
            <div className="flex justify-center py-4">
              <LoadingSpinner size={36} />
            </div>
          ) : linkedContacts.length === 0 ? (
            <div className="text-center py-4 text-gray-500">
              No contacts linked to this matter yet.
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Name</TableHead>
                  <TableHead>Designation</TableHead>
                  <TableHead>Contact Info</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {linkedContacts.map((link, index) => {
                  console.log("link", link);

                  // Handle different possible data structures
                  const contact = link?.contact || link || {};
                  const contactInfo = link?.contactInfo || {};
                  
                  // Only access designation directly from the link object
                  let designation = '-';
                  if (link && typeof link === 'object' && 'designation' in link) {
                    console.log("designation");
                    designation = link.designation || '-';
                  }
                  
                  return (
                    <TableRow key={contact?.contactId || `row-${index}`}>
                      <TableCell>
                        {contact?.firstName || ''} {contact?.middleName ? `${contact.middleName} ` : ''}
                        {contact.lastName || ''} 
                        {(contact.firstName || contact.middleName || contact.lastName) && contact.companyName ? ", " : ""} 
                        {contact?.companyName || ''}
                      </TableCell>
                      <TableCell>{designation}</TableCell>
                      <TableCell>
                        <div className="space-y-1">
                          {link?.primary_phone?.value && (
                            <div className="text-sm">
                              <span className="font-medium">Phone:</span> {link.primary_phone.value}
                            </div>
                          )}
                          {link?.primary_email?.value && (
                            <div className="text-sm">
                              <span className="font-medium">Email:</span> {link.primary_email.value}
                            </div>
                          )}
                        </div>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      {/* Back Button */}
      <div className="flex justify-start">
        <Button onClick={handleBack}>Back to Home</Button>
      </div>
    </div>
  );
}

export default MatterDetail;